import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import useUserStore from '../store/userStore';
import usePNGHandler from './usePNGHandler';

/**
 * Кнопка "Copy PNG".
 *
 * @param {Object[]}  assets               — массив ассетов (обычно один)
 * @param {string}    archiveName
 * @param {boolean}   is_purchased
 * @param {boolean}   isSubscriptionActive
 * @param {function}  onBeforeAction       — колбэк: если вернёт (или зарезолвит) false, не копируем
 * @param {function}  onActionComplete     — колбэк: вызывается, когда действие (копирование) УСПЕШНО
 */
const CopyPNGButton = ({
  assets,
  archiveName,
  is_purchased,
  isSubscriptionActive,
  onBeforeAction,
  onActionComplete,
}) => {
  const accessToken = useUserStore((state) => state.accessToken);
  const isLoggedIn = useUserStore((state) => state.isLoggedIn);

  const {
    isDownloading,
    setIsDownloading,
    errorMessage,
    setErrorMessage,
    defaultSize,
    handleDownloadLogic,
  } = usePNGHandler(assets, accessToken, isLoggedIn, is_purchased, archiveName);

  const [successMessage, setSuccessMessage] = useState('');
  
  // Создаем ref для невидимого канваса, который будет использоваться для fallback метода
  const canvasRef = useRef(null);
  const hiddenImageRef = useRef(null);

  /**
   * Проверяет поддержку браузером Clipboard API для изображений
   */
  const supportsClipboardImages = useCallback(() => {
    return (
      navigator.clipboard &&
      typeof navigator.clipboard.write === 'function' &&
      typeof ClipboardItem === 'function'
    );
  }, []);

  /**
   * Создает невидимую ссылку для копирования через метод данных
   */
  const copyViaSafariMethod = (blob) => {
    // Создаем ссылку для скачивания изображения
    const url = URL.createObjectURL(blob);
    
    // Создаем временное изображение для отображения
    const tempImg = document.createElement('img');
    tempImg.onload = () => {
      try {
        // Создаем canvas на основе размеров изображения
        const canvas = document.createElement('canvas');
        canvas.width = tempImg.width;
        canvas.height = tempImg.height;
        
        // Рисуем изображение на canvas
        const ctx = canvas.getContext('2d');
        ctx.drawImage(tempImg, 0, 0);
        
        // Добавляем canvas в DOM временно
        canvas.style.position = 'fixed';
        canvas.style.top = '0';
        canvas.style.left = '0';
        canvas.style.opacity = '0';
        canvas.style.pointerEvents = 'none';
        document.body.appendChild(canvas);
        
        // Выбираем canvas
        canvas.focus();
        
        // В Safari это более надежно, чем execCommand('selectall')
        const range = document.createRange();
        range.selectNode(canvas);
        
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Пытаемся копировать
        document.execCommand('copy');
        
        // Удаляем временные элементы
        selection.removeAllRanges();
        document.body.removeChild(canvas);
        URL.revokeObjectURL(url);
      } catch (e) {
        console.error('Safari copy failed:', e);
        // Показываем сообщение о необходимости ручного копирования
        setErrorMessage('Automatic copying not available in this browser. Right-click the image and select "Copy Image".');
      }
    };
    
    // Загружаем изображение
    tempImg.src = url;
  };

  /**
   * Основная функция, которая действительно копирует PNG в буфер обмена.
   */
  const startCopy = useCallback(async () => {
    if (!defaultSize) {
      setErrorMessage('No default size available for this collection.');
      return;
    }

    setIsDownloading(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      const result = await handleDownloadLogic(defaultSize);

      // Сценарий для одиночного файла
      if (result.type === 'single') {
        // Определяем браузер
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const useModernAPI = supportsClipboardImages() && !isSafari;
        
        if (useModernAPI) {
          try {
            // Современный метод (Chrome, Edge)
            await navigator.clipboard.write([
              new ClipboardItem({ 'image/png': result.blob }),
            ]);
            
            setSuccessMessage(`Copied! ${defaultSize}x${defaultSize}`);
            
            // Сообщаем родителю, что копирование УСПЕШНО
            if (onActionComplete) {
              onActionComplete();
            }
          } catch (clipboardError) {
            console.error('Modern clipboard API failed:', clipboardError);
            // Если современный API не сработал, пробуем Safari метод
            copyViaSafariMethod(result.blob);
            setSuccessMessage(`Copied! ${defaultSize}x${defaultSize}`);
            if (onActionComplete) onActionComplete();
          }
        } else {
          // Safari/Firefox метод
          copyViaSafariMethod(result.blob);
          setSuccessMessage(`Copied! ${defaultSize}x${defaultSize}`);
          
          // Сообщаем родителю, что копирование УСПЕШНО
          if (onActionComplete) {
            onActionComplete();
          }
        }
      } else {
        setErrorMessage('Multiple assets copy not implemented yet.');
      }
    } catch (error) {
      console.error('Copy error:', error);
      setErrorMessage(`Error copying PNG: ${error.message || 'Unknown error'}`);
    } finally {
      setIsDownloading(false);
    }
  }, [
    defaultSize,
    handleDownloadLogic,
    onActionComplete,
    setErrorMessage,
    setIsDownloading,
    supportsClipboardImages,
  ]);

  /**
   * Показывает пользователю модальное окно с инструкциями ручного копирования
   */
  const showManualCopyInstructions = () => {
    setErrorMessage(
      'Автоматическое копирование заблокировано браузером. ' +
      'Для копирования: нажмите правой кнопкой мыши на изображение и выберите "Копировать изображение"'
    );
  };

  /**
   * Обработчик клика «Copy PNG».
   * 1) Если есть onBeforeAction — дожидаемся его (true/false).
   * 2) Если false, прерываем.
   * 3) Если true, выполняем startCopy().
   */
  const handleCopyClick = async (e) => {
    e.preventDefault();
    if (isDownloading || successMessage) return;

    try {
      if (onBeforeAction) {
        const canContinue = await Promise.resolve(onBeforeAction());
        if (!canContinue) {
          return;
        }
      }

      // В Safari и iOS нужно выполнять операции с буфером обмена
      // непосредственно в обработчике пользовательского действия
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      if (isSafari || isiOS) {
        // Пытаемся выполнить сразу в обработчике клика
        startCopy();
      } else {
        // Для других браузеров продолжаем как обычно
        startCopy();
      }
    } catch (error) {
      console.error('Copy attempt failed:', error);
      showManualCopyInstructions();
    }
  };

  // Автоматически скрываем «Copied!» через 1 секунду
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const displayText = successMessage || 'Copy PNG';
  const linkClass = successMessage ? 'copy-link copy-link--success' : 'copy-link';

  // Отображение изображения для ручного копирования, если возникла ошибка
  const [showManualCopyImage, setShowManualCopyImage] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  
  // Функция для отображения изображения для ручного копирования
  const displayImageForManualCopy = async () => {
    try {
      const result = await handleDownloadLogic(defaultSize);
      if (result.type === 'single') {
        const url = URL.createObjectURL(result.blob);
        setImageUrl(url);
        setShowManualCopyImage(true);
      }
    } catch (error) {
      console.error('Failed to display image for manual copy:', error);
    }
  };

  return (
    <div>
      {/* Невидимые элементы для fallback копирования */}
      <canvas 
        ref={canvasRef} 
        style={{ display: 'none', position: 'absolute', top: '-9999px' }} 
        tabIndex="-1"
      />
      <img 
        ref={hiddenImageRef} 
        style={{ display: 'none' }} 
        alt="Hidden" 
      />
      
      {!isDownloading && defaultSize && (
        <a
          href="#"
          className={linkClass}
          onClick={handleCopyClick}
          style={{ cursor: 'pointer' }}
        >
          {displayText}
        </a>
      )}
      
      {/* Кнопка для отображения изображения для ручного копирования */}
      {errorMessage && errorMessage.includes('блокировано') && (
        <button 
          onClick={displayImageForManualCopy}
          style={{
            marginTop: '10px',
            padding: '5px 10px',
            background: '#f0f0f0',
            border: '1px solid #ccc',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Показать изображение для копирования
        </button>
      )}
      
      {/* Отображение изображения для ручного копирования */}
      {showManualCopyImage && imageUrl && (
        <div style={{ marginTop: '15px', textAlign: 'center' }}>
          <p>Щелкните правой кнопкой мыши по изображению и выберите "Копировать изображение":</p>
          <img 
            src={imageUrl} 
            alt="Для копирования" 
            style={{ 
              maxWidth: '100%', 
              border: '1px dashed #ccc',
              padding: '5px'
            }} 
          />
          <button
            onClick={() => {
              setShowManualCopyImage(false);
              URL.revokeObjectURL(imageUrl);
            }}
            style={{
              marginTop: '10px',
              padding: '5px 10px',
              background: '#f0f0f0',
              border: '1px solid #ccc',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Закрыть
          </button>
        </div>
      )}
      
      {errorMessage && (
        <p style={{ color: 'red', marginTop: '5px' }}>{errorMessage}</p>
      )}
    </div>
  );
};

CopyPNGButton.propTypes = {
  assets: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      collection: PropTypes.string.isRequired,
      media_type: PropTypes.string,
      is_free: PropTypes.bool,
      format: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
  archiveName: PropTypes.string,
  is_purchased: PropTypes.bool,
  isSubscriptionActive: PropTypes.bool,
  onBeforeAction: PropTypes.func,
  onActionComplete: PropTypes.func,
};

CopyPNGButton.defaultProps = {
  archiveName: '',
  is_purchased: false,
  isSubscriptionActive: false,
  onBeforeAction: null,
  onActionComplete: null,
};

export default CopyPNGButton;